// Desktop "+" dropdown for the chat composer (Claude-style anchored menu).
// Main view: attachment sources + a Reasoning row that drills into the
// reasoning options in place.
import { useEffect, useState } from "react";
import { Box, Divider, ListItemIcon, Menu, MenuItem } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { isDarkMode } from "../../utils/constants";
import { REASONING_OPTIONS, reasoningLabelFor } from "./reasoningModes";

const ComposerPlusMenu = ({
  anchorEl,
  open,
  onClose,
  showReasoning,
  reasoningMode,
  onSelectReasoning,
  onPickPhotos,
  onPickFiles,
}) => {
  const [view, setView] = useState("main");

  // Every open starts on the main page, even if it was closed mid-drill-in.
  useEffect(() => {
    if (open) setView("main");
  }, [open]);

  const textColor = isDarkMode ? "#f5f5f5" : "#121212";
  const mutedColor = isDarkMode
    ? "rgba(255,255,255,0.55)"
    : "rgba(18,18,18,0.55)";
  const maxLabelColor = isDarkMode ? "#FCD34D" : "#92400E";

  const itemSx = {
    fontSize: "0.9rem",
    fontWeight: 500,
    color: textColor,
    py: 1,
    px: 1.5,
    borderRadius: "10px",
    mx: 0.75,
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            mt: -1,
            minWidth: 280,
            borderRadius: "16px",
            backgroundColor: isDarkMode ? "#202020" : "#ffffff",
            border: isDarkMode
              ? "1px solid rgba(255,255,255,0.08)"
              : "1px solid rgba(0,0,0,0.06)",
            boxShadow: isDarkMode
              ? "0 12px 32px rgba(0,0,0,0.45)"
              : "0 12px 28px rgba(15,23,42,0.16)",
          },
        },
      }}
    >
      {view === "main"
        ? [
            <MenuItem
              key="photos"
              sx={itemSx}
              onClick={() => {
                onClose();
                onPickPhotos();
              }}
            >
              <ListItemIcon sx={{ color: textColor }}>
                <ImageOutlinedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              Add photos
            </MenuItem>,
            <MenuItem
              key="files"
              sx={itemSx}
              onClick={() => {
                onClose();
                onPickFiles();
              }}
            >
              <ListItemIcon sx={{ color: textColor }}>
                <UploadFileOutlinedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              Add files
            </MenuItem>,
            ...(showReasoning
              ? [
                  <Divider
                    key="divider"
                    sx={{
                      my: 0.5,
                      borderColor: isDarkMode
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                    }}
                  />,
                  <MenuItem
                    key="reasoning"
                    sx={itemSx}
                    onClick={() => setView("reasoning")}
                  >
                    <ListItemIcon sx={{ color: textColor }}>
                      <BoltRoundedIcon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    Reasoning
                    <Box
                      sx={{
                        ml: "auto",
                        pl: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        color: mutedColor,
                        fontSize: "0.84rem",
                      }}
                    >
                      {reasoningLabelFor(reasoningMode)}
                      <ChevronRightIcon sx={{ fontSize: 18 }} />
                    </Box>
                  </MenuItem>,
                ]
              : []),
          ]
        : [
            <MenuItem key="back" sx={itemSx} onClick={() => setView("main")}>
              <ListItemIcon sx={{ color: textColor }}>
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              Reasoning
            </MenuItem>,
            <Divider
              key="divider"
              sx={{
                my: 0.5,
                borderColor: isDarkMode
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              }}
            />,
            ...REASONING_OPTIONS.map((option) => {
              const isSelected = option.value === reasoningMode;
              const isMax = option.value === "max";
              return (
                <MenuItem
                  key={option.value}
                  sx={{
                    ...itemSx,
                    alignItems: "center",
                    backgroundColor: isSelected
                      ? isDarkMode
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.045)"
                      : "transparent",
                  }}
                  onClick={() => {
                    onClose();
                    onSelectReasoning(option.value);
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        fontWeight: 600,
                        color: isMax ? maxLabelColor : textColor,
                      }}
                    >
                      {option.label}
                      {isMax && (
                        <BoltRoundedIcon
                          sx={{ fontSize: 15, color: "#F59E0B" }}
                        />
                      )}
                    </Box>
                    <Box sx={{ fontSize: "0.74rem", color: mutedColor }}>
                      {option.description}
                    </Box>
                  </Box>
                  {isSelected && (
                    <CheckRoundedIcon
                      sx={{ ml: "auto", pl: 1, fontSize: 22, color: textColor }}
                    />
                  )}
                </MenuItem>
              );
            }),
          ]}
    </Menu>
  );
};

export default ComposerPlusMenu;
